import React, { useState, useEffect, useContext } from 'react';
import ReactDOM from 'react-dom';
import styles from './UserSettingsModal.module.css';
import ChangePasswordModal from './ChangePasswordModal';
import { AuthContext } from '../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://wsticket.frozentoasty.de/api';

function UserSettingsModal({ isOpen, onClose }) {
  const { currentUser } = useContext(AuthContext);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [backgrounds, setBackgrounds] = useState([]);
  const [selectedBackground, setSelectedBackground] = useState('');
  const [backgroundBrightness, setBackgroundBrightness] = useState(100);
  const [zohoEmail, setZohoEmail] = useState('');
  const [zohoEmailSaving, setZohoEmailSaving] = useState(false);
  const [success, setSuccess] = useState('');

  // Admin-specific states
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newUserRole, setNewUserRole] = useState('user');

  // LLM Provider states
  const [llmProviders, setLlmProviders] = useState([]);
  const [llmLoading, setLlmLoading] = useState(false);
  const [llmTesting, setLlmTesting] = useState(null);
  const [showAddProvider, setShowAddProvider] = useState(false);
  const [newProvider, setNewProvider] = useState({ name: '', apiUrl: '', modelName: '', apiKey: '' });
  const [editingProvider, setEditingProvider] = useState(null);
  // Zoho Books Verbindung states
  const [zohoStatus, setZohoStatus] = useState(null);
  const [zohoForm, setZohoForm] = useState({ clientId: '', clientSecret: '', organizationId: '' });
  const [zohoSaving, setZohoSaving] = useState(false);
  const [zohoConnecting, setZohoConnecting] = useState(false);

  // AI Prompt states
  const [aiPrompts, setAiPrompts] = useState({ ticket: { default: '', custom: '' }, rapport: { default: '', custom: '' } });
  const [editingPromptType, setEditingPromptType] = useState(null);
  const [editingPromptValue, setEditingPromptValue] = useState('');
  const [savingPrompt, setSavingPrompt] = useState(false);

  // External API Key states
  const [externalApiKey, setExternalApiKey] = useState({ hasKey: false, preview: null });
  const [generatedApiKey, setGeneratedApiKey] = useState(null);
  const [apiKeyLoading, setApiKeyLoading] = useState(false);

  // Agent API Key states (for all users)
  const [agentApiKey, setAgentApiKey] = useState({ hasKey: false, partialKey: null, lastUsed: null });
  const [generatedAgentKey, setGeneratedAgentKey] = useState(null);
  const [agentKeyLoading, setAgentKeyLoading] = useState(false);
  const [revealedAgentKey, setRevealedAgentKey] = useState(null);
  const [agentKeyRevealing, setAgentKeyRevealing] = useState(false);

  useEffect(() => {
    if (isOpen) {
      console.log('UserSettingsModal opened!');
      setIsClosing(false);
      loadBackgrounds();
      loadUserBackground();
      // Load users and LLM settings if admin
      if (currentUser?.role === 'admin') {
        fetchUsers();
        fetchLLMSettings();
        fetchAIPrompts();
        fetchExternalApiKey();
        fetchZohoConfig();
      }
      // Reset generated key display when modal opens
      setGeneratedApiKey(null);
      setGeneratedAgentKey(null);
      fetchAgentApiKey();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const loadUserBackground = async () => {
    try {
      const response = await axios.get(`${API_URL}/users/preferences`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const bgImage = response.data.selectedBackground || 'background9.png';
      const brightness = response.data.backgroundBrightness !== undefined ? response.data.backgroundBrightness : 100;
      setSelectedBackground(bgImage);
      setBackgroundBrightness(brightness);
      setZohoEmail(response.data.zohoEmail || '');
    } catch (err) {
      console.error('Error loading user background:', err);
      // Fallback to localStorage
      const savedBg = localStorage.getItem('selectedBackground') || 'background9.png';
      const savedBrightness = parseInt(localStorage.getItem('backgroundBrightness') || '100');
      setSelectedBackground(savedBg);
      setBackgroundBrightness(savedBrightness);
    }
  };

  const loadBackgrounds = async () => {
    try {
      // List of available background images
      // Add new backgrounds here when you add them to public/background/
      const availableBackgrounds = [
        'background1.png',
        'background2.png',
        'background3.png',
        'background9.png',
        'background10.png',
        'background11.png'
      ];

      // Verify which backgrounds actually exist by trying to load them
      const validBackgrounds = [];
      for (const bg of availableBackgrounds) {
        try {
          const response = await fetch(`/background/${bg}`, { method: 'HEAD' });
          if (response.ok) {
            validBackgrounds.push(bg);
          }
        } catch {
          console.log(`Background ${bg} not found, skipping...`);
        }
      }

      console.log('Loaded backgrounds:', validBackgrounds.length > 0 ? validBackgrounds : availableBackgrounds);
      setBackgrounds(validBackgrounds.length > 0 ? validBackgrounds : availableBackgrounds);
    } catch (err) {
      console.error('Error loading backgrounds:', err);
      // Fallback to hardcoded list
      setBackgrounds([
        'background1.png',
        'background2.png',
        'background9.png',
        'background10.png',
        'background11.png'
      ]);
    }
  };

  const handleBackgroundSelect = async (bgName) => {
    setSelectedBackground(bgName);
    localStorage.setItem('selectedBackground', bgName);
    // Apply the background immediately to bucket container
    const container = document.querySelector('.bucket-container-scroll');
    if (container) {
      container.style.backgroundImage = `url(/background/${bgName})`;
    }

    // Save to database
    try {
      await axios.put(`${API_URL}/users/preferences/background`, {
        selectedBackground: bgName
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setSuccess(`Hintergrund "${bgName}" wurde gespeichert`);
    } catch (err) {
      console.error('Error saving background:', err);
      setSuccess(`Hintergrund "${bgName}" wurde lokal gespeichert (Fehler beim Speichern in DB)`);
    }
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleBrightnessChange = async (brightness) => {
    setBackgroundBrightness(brightness);
    localStorage.setItem('backgroundBrightness', brightness.toString());

    // Apply brightness immediately to bucket container using CSS variable
    const container = document.querySelector('.bucket-container-scroll');
    if (container) {
      container.style.setProperty('--bg-brightness', brightness);
    }

    // Save to database
    try {
      await axios.put(`${API_URL}/users/preferences/brightness`, {
        backgroundBrightness: brightness
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
    } catch (err) {
      console.error('Error saving brightness:', err);
    }
  };

  const handleZohoEmailSave = async () => {
    setZohoEmailSaving(true);
    setError('');
    try {
      await axios.put(`${API_URL}/users/preferences/zoho-email`, {
        zohoEmail: zohoEmail.trim()
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setSuccess(zohoEmail.trim() ? 'Zoho E-Mail wurde gespeichert' : 'Zoho E-Mail wurde entfernt');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Speichern der Zoho E-Mail');
    } finally {
      setZohoEmailSaving(false);
    }
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 150);
  };

  // Admin functions
  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_URL}/admin/users`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setUsers(response.data);
    } catch (err) {
      setError('Fehler beim Laden der Benutzer');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!newUsername.trim() || !newPassword.trim()) {
      setError('Benutzername und Passwort sind erforderlich');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await axios.post(`${API_URL}/admin/users`, {
        username: newUsername.trim(),
        password: newPassword,
        role: newUserRole
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      setSuccess(`Benutzer "${newUsername}" wurde erfolgreich erstellt`);
      setNewUsername('');
      setNewPassword('');
      setNewUserRole('user');
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Erstellen des Benutzers');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (username) => {
    if (!window.confirm(`Möchten Sie den Benutzer "${username}" wirklich löschen?`)) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await axios.delete(`${API_URL}/admin/users/${username}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      setSuccess(`Benutzer "${username}" wurde erfolgreich gelöscht`);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Löschen des Benutzers');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleUserStatus = async (username, currentStatus) => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await axios.patch(`${API_URL}/admin/users/${username}/status`, {
        active: !currentStatus
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      setSuccess(`Benutzer "${username}" wurde ${!currentStatus ? 'aktiviert' : 'deaktiviert'}`);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Ändern des Benutzerstatus');
    } finally {
      setLoading(false);
    }
  };


  // Zoho Books Verbindung functions
  const fetchZohoConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/zoho/config`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setZohoStatus(response.data);
    } catch (err) {
      console.error('Error fetching zoho config:', err);
    }
  };

  const saveZohoConfig = async () => {
    setZohoSaving(true);
    try {
      const body = {};
      if (zohoForm.clientId.trim()) body.clientId = zohoForm.clientId.trim();
      if (zohoForm.clientSecret.trim()) body.clientSecret = zohoForm.clientSecret.trim();
      if (zohoForm.organizationId.trim()) body.organizationId = zohoForm.organizationId.trim();
      const response = await axios.put(`${API_URL}/zoho/config`, body, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setSuccess(response.data.message || 'Zoho Einstellungen gespeichert');
      setZohoForm({ clientId: '', clientSecret: '', organizationId: '' });
      fetchZohoConfig();
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Speichern der Zoho Einstellungen');
    } finally {
      setZohoSaving(false);
    }
  };

  const connectZoho = async () => {
    setZohoConnecting(true);
    try {
      const response = await axios.get(`${API_URL}/zoho/oauth/url`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.data.url) {
        window.open(response.data.url, '_blank');
        setTimeout(fetchZohoConfig, 10000);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Starten der Zoho Verbindung');
    } finally {
      setZohoConnecting(false);
    }
  };

  // LLM Provider functions
  const fetchLLMSettings = async () => {
    setLlmLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/llm-providers`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setLlmProviders(response.data);
    } catch (err) {
      console.error('Error fetching LLM providers:', err);
    } finally {
      setLlmLoading(false);
    }
  };

  const handleAddProvider = async (e) => {
    e.preventDefault();
    if (!newProvider.name.trim() || !newProvider.apiUrl.trim() || !newProvider.modelName.trim() || !newProvider.apiKey.trim()) {
      setError('Alle Felder sind erforderlich');
      return;
    }

    setLlmLoading(true);
    setError('');
    setSuccess('');

    try {
      await axios.post(`${API_URL}/admin/llm-providers`, newProvider, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      setSuccess(`Provider "${newProvider.name}" wurde erstellt`);
      setNewProvider({ name: '', apiUrl: '', modelName: '', apiKey: '' });
      setShowAddProvider(false);
      fetchLLMSettings();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Erstellen des Providers');
    } finally {
      setLlmLoading(false);
    }
  };

  const handleActivateProvider = async (providerId) => {
    setLlmLoading(true);
    setError('');
    setSuccess('');

    try {
      await axios.put(`${API_URL}/admin/llm-providers/${providerId}/activate`, {}, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      setSuccess('Provider wurde aktiviert');
      fetchLLMSettings();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Aktivieren des Providers');
    } finally {
      setLlmLoading(false);
    }
  };

  const handleDeleteProvider = async (providerId, providerName) => {
    if (!window.confirm(`Provider "${providerName}" wirklich löschen?`)) return;

    setLlmLoading(true);
    setError('');
    setSuccess('');

    try {
      await axios.delete(`${API_URL}/admin/llm-providers/${providerId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      setSuccess(`Provider "${providerName}" wurde gelöscht`);
      fetchLLMSettings();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Löschen des Providers');
    } finally {
      setLlmLoading(false);
    }
  };

  const handleTestProvider = async (providerId) => {
    setLlmTesting(providerId);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post(`${API_URL}/admin/llm-providers/${providerId}/test`, {}, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setSuccess(`Verbindung erfolgreich: ${response.data.response || 'OK'}`);
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.error || `Verbindung fehlgeschlagen: ${err.response?.data?.details || err.message}`);
    } finally {
      setLlmTesting(null);
    }
  };

  const handleEditProvider = (provider) => {
    setEditingProvider({
      _id: provider._id,
      name: provider.name,
      apiUrl: provider.apiUrl,
      modelName: provider.modelName,
      apiKey: '',
      maskedApiKey: provider.maskedApiKey || ''
    });
    setShowAddProvider(false);
  };

  const handleSaveEditProvider = async (e) => {
    e.preventDefault();
    if (!editingProvider) return;

    setLlmLoading(true);
    setError('');
    setSuccess('');

    try {
      const updateData = {
        name: editingProvider.name,
        apiUrl: editingProvider.apiUrl,
        modelName: editingProvider.modelName
      };
      if (editingProvider.apiKey) {
        updateData.apiKey = editingProvider.apiKey;
      }

      await axios.put(`${API_URL}/admin/llm-providers/${editingProvider._id}`, updateData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      setSuccess(`Provider "${editingProvider.name}" wurde aktualisiert`);
      setEditingProvider(null);
      fetchLLMSettings();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Aktualisieren des Providers');
    } finally {
      setLlmLoading(false);
    }
  };

  const handleCancelEditProvider = () => {
    setEditingProvider(null);
  };

  // AI Prompt functions
  const fetchAIPrompts = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/ai-prompts`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setAiPrompts(response.data);
    } catch (err) {
      console.error('Error fetching AI prompts:', err);
    }
  };

  const handleEditPrompt = (type) => {
    setEditingPromptType(type);
    setEditingPromptValue(aiPrompts[type]?.custom || '');
  };

  const handleSavePrompt = async () => {
    if (!editingPromptType) return;

    setSavingPrompt(true);
    setError('');
    setSuccess('');

    try {
      await axios.put(`${API_URL}/admin/ai-prompts/${editingPromptType}`, {
        customPrompt: editingPromptValue
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      setSuccess(`AI ${editingPromptType === 'ticket' ? 'Ticket' : 'Rapport'} Prompt wurde gespeichert`);
      fetchAIPrompts();
      setEditingPromptType(null);
      setEditingPromptValue('');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Speichern des Prompts');
    } finally {
      setSavingPrompt(false);
    }
  };

  const handleCancelEditPrompt = () => {
    setEditingPromptType(null);
    setEditingPromptValue('');
  };

  // Agent API Key functions (for all users)
  const fetchAgentApiKey = async () => {
    try {
      const response = await axios.get(`${API_URL}/users/agent-api-key`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setAgentApiKey(response.data);
    } catch (err) {
      console.error('Error fetching agent API key:', err);
    }
  };

  const handleGenerateAgentKey = async () => {
    if (agentApiKey.hasKey && !window.confirm('Neuen Agent API Key generieren? Der alte Key wird ungültig!')) {
      return;
    }
    setAgentKeyLoading(true);
    setError('');
    setSuccess('');
    try {
      const response = await axios({
        method: agentApiKey.hasKey ? 'put' : 'post',
        url: `${API_URL}/users/agent-api-key`,
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setGeneratedAgentKey(response.data.apiKey);
      setRevealedAgentKey(null);
      setAgentApiKey({ hasKey: true, partialKey: response.data.apiKey.substring(0, 12) + '...' + response.data.apiKey.slice(-8) });
      setSuccess('Agent API Key generiert! Jetzt kopieren — wird nicht erneut angezeigt.');
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Generieren des Agent API Keys');
    } finally {
      setAgentKeyLoading(false);
    }
  };

  const handleRevealAgentKey = async () => {
    if (revealedAgentKey) {
      setRevealedAgentKey(null);
      return;
    }
    setAgentKeyRevealing(true);
    setError('');
    setSuccess('');
    try {
      const response = await axios.get(`${API_URL}/users/agent-api-key/reveal`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setRevealedAgentKey(response.data.apiKey);
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Anzeigen des Agent API Keys');
    } finally {
      setAgentKeyRevealing(false);
    }
  };

  const handleCopyRevealedAgentKey = () => {
    if (revealedAgentKey) {
      navigator.clipboard.writeText(revealedAgentKey);
      setSuccess('Agent API Key kopiert!');
      setTimeout(() => setSuccess(''), 3000);
    }
  };

  const handleRevokeAgentKey = async () => {
    if (!window.confirm('Agent API Key widerrufen? Alle Agents die diesen Key nutzen verlieren den Zugriff.')) {
      return;
    }
    setAgentKeyLoading(true);
    try {
      await axios.delete(`${API_URL}/users/agent-api-key`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setAgentApiKey({ hasKey: false, partialKey: null, lastUsed: null });
      setGeneratedAgentKey(null);
      setRevealedAgentKey(null);
      setSuccess('Agent API Key widerrufen.');
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Widerrufen');
    } finally {
      setAgentKeyLoading(false);
    }
  };

  const handleCopyAgentKey = () => {
    if (generatedAgentKey) {
      navigator.clipboard.writeText(generatedAgentKey);
      setSuccess('Agent API Key kopiert!');
      setTimeout(() => setSuccess(''), 3000);
    }
  };

  // External API Key functions
  const fetchExternalApiKey = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/external-api-key`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setExternalApiKey(response.data);
    } catch (err) {
      console.error('Error fetching external API key:', err);
    }
  };

  const handleGenerateApiKey = async () => {
    if (externalApiKey.hasKey && !window.confirm('Neuen API Key generieren? Der alte Key wird damit ungültig!')) {
      return;
    }

    setApiKeyLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post(`${API_URL}/admin/external-api-key/generate`, {}, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      setGeneratedApiKey(response.data.apiKey);
      setExternalApiKey({ hasKey: true, preview: '***' + response.data.apiKey.slice(-4) });
      setSuccess('API Key generiert! Kopieren Sie ihn jetzt - er wird nicht erneut angezeigt.');
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Generieren des API Keys');
    } finally {
      setApiKeyLoading(false);
    }
  };

  const handleCopyApiKey = () => {
    if (generatedApiKey) {
      navigator.clipboard.writeText(generatedApiKey);
      setSuccess('API Key in Zwischenablage kopiert!');
      setTimeout(() => setSuccess(''), 3000);
    }
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className={`${styles.overlay} ${isClosing ? styles.closing : ''}`}>
      <div className={`${styles.modal} ${isClosing ? styles.closing : ''}`}>
        <div className={styles.header}>
          <h2>Einstellungen</h2>
          <button className={styles.closeButton} onClick={handleClose}>×</button>
        </div>

        <div className={styles.content}>
          {/* Background Selection Section */}
          <div className={styles.section}>
            <h3>Hintergrund auswählen</h3>
            <div className={styles.backgroundGrid}>
              {backgrounds.map((bg) => (
                <div
                  key={bg}
                  className={`${styles.backgroundItem} ${selectedBackground === bg ? styles.selected : ''}`}
                  onClick={() => handleBackgroundSelect(bg)}
                >
                  <img
                    src={`/background/${bg}`}
                    alt={bg}
                    className={styles.backgroundPreview}
                  />
                  <div className={styles.backgroundName}>{bg}</div>
                  {selectedBackground === bg && (
                    <div className={styles.selectedBadge}>✓</div>
                  )}
                </div>
              ))}
            </div>
            {backgrounds.length === 0 && (
              <p style={{color: '#ccc', fontSize: '0.9rem'}}>Keine Hintergründe gefunden</p>
            )}
          </div>

          {/* Brightness Control Section */}
          <div className={styles.section}>
            <h3>Hintergrund-Helligkeit</h3>
            <div className={styles.brightnessControl}>
              <input
                type="range"
                min="0"
                max="200"
                value={backgroundBrightness}
                onChange={(e) => handleBrightnessChange(parseInt(e.target.value))}
                className={styles.brightnessSlider}
              />
              <span className={styles.brightnessValue}>{backgroundBrightness}%</span>
            </div>
            <p className={styles.brightnessHint}>
              0% = Schwarz, 100% = Normal, 200% = Sehr hell
            </p>
          </div>

          {/* Password Change Section */}
          <div className={styles.section}>
            <h3>Passwort ändern</h3>
            <button
              className={styles.changePasswordButton}
              onClick={() => setIsChangePasswordOpen(true)}
            >
              Passwort ändern
            </button>
          </div>

          {/* Zoho Email Section */}
          <div className={styles.section}>
            <h3>Zoho Books E-Mail</h3>
            <p className={styles.sectionHint}>
              Diese E-Mail wird für Zeiteinträge in Zoho Books verwendet.
              Ohne hinterlegte E-Mail kann kein AI Rapport erstellt werden.
            </p>
            <div className={styles.zohoEmailContainer}>
              <input
                type="email"
                value={zohoEmail}
                onChange={(e) => setZohoEmail(e.target.value)}
                placeholder="deine.email@zoho.com"
                className={styles.zohoEmailInput}
              />
              <button
                onClick={handleZohoEmailSave}
                className={styles.zohoEmailButton}
                disabled={zohoEmailSaving}
              >
                {zohoEmailSaving ? 'Speichern...' : 'Speichern'}
              </button>
            </div>
            {zohoEmail && (
              <p className={styles.zohoEmailStatus}>
                ✓ Zoho E-Mail hinterlegt
              </p>
            )}
          </div>

          {/* Agent API Key Section (for all users) */}
          <div className={styles.section}>
            <h3>Agent API Key</h3>
            <p className={styles.sectionHint}>
              API Key für KI-Agents (Claude, SamsiBamsi, etc.) um auf deine Tickets zuzugreifen.
            </p>
            <div className={styles.apiKeyContainer}>
              <div className={styles.apiKeyStatus}>
                <span className={styles.apiKeyLabel}>Status:</span>
                {agentApiKey.hasKey ? (
                  <span className={styles.apiKeyActive}>
                    Aktiv ({agentApiKey.partialKey})
                  </span>
                ) : (
                  <span className={styles.apiKeyInactive}>Nicht generiert</span>
                )}
              </div>

              {agentApiKey.hasKey && agentApiKey.lastUsed && (
                <p className={styles.sectionHint} style={{marginTop: '4px'}}>
                  Zuletzt verwendet: {new Date(agentApiKey.lastUsed).toLocaleString('de-CH')}
                </p>
              )}

              {generatedAgentKey && (
                <div className={styles.generatedKeyBox}>
                  <p className={styles.generatedKeyLabel}>Neuer Agent API Key (nur einmalig sichtbar!):</p>
                  <div className={styles.generatedKeyValue}>
                    <code>{generatedAgentKey}</code>
                    <button
                      onClick={handleCopyAgentKey}
                      className={styles.copyButton}
                      title="Kopieren"
                    >
                      <i className="bi bi-clipboard"></i>
                    </button>
                  </div>
                </div>
              )}

              {revealedAgentKey && (
                <div className={styles.generatedKeyBox}>
                  <p className={styles.generatedKeyLabel}>Agent API Key:</p>
                  <div className={styles.generatedKeyValue}>
                    <code>{revealedAgentKey}</code>
                    <button
                      onClick={handleCopyRevealedAgentKey}
                      className={styles.copyButton}
                      title="Kopieren"
                    >
                      <i className="bi bi-clipboard"></i>
                    </button>
                  </div>
                </div>
              )}

              <div style={{display: 'flex', gap: '8px'}}>
                {agentApiKey.hasKey && (
                  <button
                    onClick={handleRevealAgentKey}
                    className={styles.generateKeyButton}
                    disabled={agentKeyRevealing}
                  >
                    {revealedAgentKey ? 'Verbergen' : (agentKeyRevealing ? 'Lade...' : 'Anzeigen')}
                  </button>
                )}
                <button
                  onClick={handleGenerateAgentKey}
                  className={styles.generateKeyButton}
                  disabled={agentKeyLoading}
                >
                  {agentKeyLoading ? 'Generiere...' : agentApiKey.hasKey ? 'Neuen Key generieren' : 'Key generieren'}
                </button>
                {agentApiKey.hasKey && (
                  <button
                    onClick={handleRevokeAgentKey}
                    className={styles.deleteButton}
                    disabled={agentKeyLoading}
                  >
                    Widerrufen
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Admin-only sections */}
          {currentUser?.role === 'admin' && (
            <>
              {/* Create User Section */}
              <div className={styles.section}>
                <h3>Neuen Benutzer erstellen</h3>
                <form onSubmit={handleCreateUser} className={styles.createUserForm}>
                  <div className={styles.inputGroup}>
                    <label>Benutzername:</label>
                    <input
                      type="text"
                      value={newUsername}
                      onChange={(e) => setNewUsername(e.target.value)}
                      placeholder="Benutzername eingeben"
                      required
                    />
                  </div>
                  <div className={styles.inputGroup}>
                    <label>Passwort:</label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Passwort eingeben"
                      required
                    />
                  </div>
                  <div className={styles.inputGroup}>
                    <label>Rolle:</label>
                    <select
                      value={newUserRole}
                      onChange={(e) => setNewUserRole(e.target.value)}
                    >
                      <option value="user">Benutzer</option>
                      <option value="admin">Administrator</option>
                    </select>
                  </div>
                  <button
                    type="submit"
                    className={styles.createButton}
                    disabled={loading}
                  >
                    {loading ? 'Erstellen...' : 'Benutzer erstellen'}
                  </button>
                </form>
              </div>

              {/* Users List Section */}
              <div className={styles.section}>
                <h3>Bestehende Benutzer</h3>
                {loading && <p>Lade Benutzer...</p>}

                {!loading && users.length > 0 && (
                  <div className={styles.usersList}>
                    <div className={styles.usersHeader}>
                      <span>Benutzername</span>
                      <span>Rolle</span>
                      <span>Status</span>
                      <span>Aktionen</span>
                    </div>
                    {users.map(user => (
                      <div key={user._id} className={styles.userRow}>
                        <span className={styles.username}>{user.username}</span>
                        <span className={`${styles.role} ${user.role === 'admin' ? styles.adminRole : styles.userRole}`}>
                          {user.role === 'admin' ? 'Administrator' : 'Benutzer'}
                        </span>
                        <span className={`${styles.status} ${user.active ? styles.active : styles.inactive}`}>
                          {user.active ? 'Aktiv' : 'Inaktiv'}
                        </span>
                        <div className={styles.actions}>
                          <button
                            onClick={() => handleToggleUserStatus(user.username, user.active)}
                            className={`${styles.actionButton} ${user.active ? styles.deactivateButton : styles.activateButton}`}
                            disabled={loading}
                          >
                            {user.active ? 'Deaktivieren' : 'Aktivieren'}
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user.username)}
                            className={`${styles.actionButton} ${styles.deleteButton}`}
                            disabled={loading}
                          >
                            Löschen
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {!loading && users.length === 0 && (
                  <p>Keine Benutzer gefunden.</p>
                )}
              </div>

              {/* Zoho Books Verbindung Section */}
              {currentUser?.role === 'admin' && (
                <div className={styles.section}>
                  <h3>Zoho Books Verbindung</h3>
                  <p className={styles.sectionHint}>
                    Zoho-Client für das Rapport-Absenden (Zeiteinträge in Zoho Books).
                    Redirect URI in der Zoho Console registrieren: {zohoStatus?.redirectUri || 'https://wsticket.frozentoasty.de/api/zoho/oauth/callback'}
                  </p>

                  {zohoStatus && (
                    <p className={`${styles.zohoConfigStatus} ${zohoStatus.connected ? styles.zohoConfigStatusOk : styles.zohoConfigStatusError}`}>
                      {zohoStatus.connected ? '✓ Verbunden' : '✗ Nicht verbunden'} · Client: {zohoStatus.clientId || '-'} · Org: {zohoStatus.organizationId || '-'} · Quelle: {zohoStatus.source === 'db' ? 'Admin-Einstellungen' : '.env (Fallback)'}
                    </p>
                  )}

                  <div className={styles.inputGroup}>
                    <label>Client ID</label>
                    <input
                      type="text"
                      placeholder="1000.XXXXXXXXXXXXXXXX"
                      value={zohoForm.clientId}
                      onChange={(e) => setZohoForm({ ...zohoForm, clientId: e.target.value })}
                    />
                  </div>
                  <div className={styles.inputGroup}>
                    <label>Client Secret</label>
                    <input
                      type="password"
                      placeholder="Client Secret"
                      value={zohoForm.clientSecret}
                      onChange={(e) => setZohoForm({ ...zohoForm, clientSecret: e.target.value })}
                    />
                  </div>
                  <div className={styles.inputGroup}>
                    <label>Organization ID</label>
                    <input
                      type="text"
                      placeholder="z.B. 20067512016"
                      value={zohoForm.organizationId}
                      onChange={(e) => setZohoForm({ ...zohoForm, organizationId: e.target.value })}
                    />
                  </div>

                  <div className={styles.zohoButtonRow}>
                    <button onClick={saveZohoConfig} disabled={zohoSaving} className={styles.zohoEmailButton}>
                      {zohoSaving ? 'Speichern...' : 'Speichern'}
                    </button>
                    <button onClick={connectZoho} disabled={zohoConnecting} className={styles.zohoEmailButton}>
                      {zohoConnecting ? 'Verbinde...' : 'Mit Zoho verbinden'}
                    </button>
                  </div>
                </div>
              )}

              {/* LLM Provider Section */}
              <div className={styles.section}>
                <h3>LLM / AI Provider</h3>
                <p className={styles.sectionHint}>
                  Verwalten Sie hier Ihre LLM API Provider. Der aktive Provider wird für AI Tickets und AI Rapports verwendet.
                </p>

                {llmLoading && !showAddProvider ? (
                  <p>Lade Provider...</p>
                ) : (
                  <div className={styles.llmSettings}>
                    {/* Provider List */}
                    {llmProviders.length > 0 && (
                      <div className={styles.providerList}>
                        {llmProviders.map(provider => (
                          <div key={provider._id} className={`${styles.providerItem} ${provider.isActive ? styles.activeProvider : ''}`}>
                            <div className={styles.providerInfo}>
                              <span className={styles.providerName}>
                                {provider.name}
                                {provider.isActive && <span className={styles.activeBadge}>Aktiv</span>}
                              </span>
                              <span className={styles.providerDetails}>
                                {provider.modelName}
                              </span>
                            </div>
                            <div className={styles.providerActions}>
                              <button
                                onClick={() => handleTestProvider(provider._id)}
                                className={styles.testButton}
                                disabled={llmTesting === provider._id}
                              >
                                {llmTesting === provider._id ? 'Teste...' : 'Testen'}
                              </button>
                              <button
                                onClick={() => handleEditProvider(provider)}
                                className={styles.editButton}
                                disabled={llmLoading}
                              >
                                Bearbeiten
                              </button>
                              {!provider.isActive && (
                                <>
                                  <button
                                    onClick={() => handleActivateProvider(provider._id)}
                                    className={styles.activateButton}
                                    disabled={llmLoading}
                                  >
                                    Aktivieren
                                  </button>
                                  <button
                                    onClick={() => handleDeleteProvider(provider._id, provider.name)}
                                    className={styles.deleteProviderButton}
                                    disabled={llmLoading}
                                  >
                                    Löschen
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {llmProviders.length === 0 && !showAddProvider && !editingProvider && (
                      <p className={styles.noProviders}>Keine Provider konfiguriert. Fügen Sie einen hinzu.</p>
                    )}

                    {/* Edit Provider Form */}
                    {editingProvider && (
                      <form onSubmit={handleSaveEditProvider} className={styles.addProviderForm}>
                        <h4>Provider bearbeiten: {editingProvider.name}</h4>
                        <div className={styles.inputGroup}>
                          <label>Name:</label>
                          <input
                            type="text"
                            value={editingProvider.name}
                            onChange={(e) => setEditingProvider(prev => ({ ...prev, name: e.target.value }))}
                            required
                          />
                        </div>
                        <div className={styles.inputGroup}>
                          <label>API URL:</label>
                          <input
                            type="text"
                            value={editingProvider.apiUrl}
                            onChange={(e) => setEditingProvider(prev => ({ ...prev, apiUrl: e.target.value }))}
                            required
                          />
                        </div>
                        <div className={styles.inputGroup}>
                          <label>Model Name:</label>
                          <input
                            type="text"
                            value={editingProvider.modelName}
                            onChange={(e) => setEditingProvider(prev => ({ ...prev, modelName: e.target.value }))}
                            required
                          />
                        </div>
                        <div className={styles.inputGroup}>
                          <label>API Key{editingProvider.maskedApiKey ? ` (aktuell: ${editingProvider.maskedApiKey})` : ''}:</label>
                          <input
                            type="password"
                            value={editingProvider.apiKey}
                            onChange={(e) => setEditingProvider(prev => ({ ...prev, apiKey: e.target.value }))}
                            placeholder={editingProvider.maskedApiKey ? 'Leer lassen um beizubehalten' : 'API Key eingeben'}
                          />
                        </div>
                        <div className={styles.formButtons}>
                          <button type="submit" className={styles.createButton} disabled={llmLoading}>
                            {llmLoading ? 'Speichern...' : 'Speichern'}
                          </button>
                          <button
                            type="button"
                            onClick={handleCancelEditProvider}
                            className={styles.cancelButton}
                          >
                            Abbrechen
                          </button>
                        </div>
                      </form>
                    )}

                    {/* Add Provider Button/Form */}
                    {!showAddProvider && !editingProvider ? (
                      <button
                        onClick={() => setShowAddProvider(true)}
                        className={styles.addProviderButton}
                      >
                        + Neuen Provider hinzufügen
                      </button>
                    ) : !editingProvider && (
                      <form onSubmit={handleAddProvider} className={styles.addProviderForm}>
                        <h4>Neuer Provider</h4>
                        <div className={styles.inputGroup}>
                          <label>Name:</label>
                          <input
                            type="text"
                            value={newProvider.name}
                            onChange={(e) => setNewProvider(prev => ({ ...prev, name: e.target.value }))}
                            placeholder="z.B. OpenAI, Local LLM, z.ai"
                            required
                          />
                        </div>
                        <div className={styles.inputGroup}>
                          <label>API URL:</label>
                          <input
                            type="text"
                            value={newProvider.apiUrl}
                            onChange={(e) => setNewProvider(prev => ({ ...prev, apiUrl: e.target.value }))}
                            placeholder="https://api.example.com/v1/chat/completions"
                            required
                          />
                        </div>
                        <div className={styles.inputGroup}>
                          <label>Model Name:</label>
                          <input
                            type="text"
                            value={newProvider.modelName}
                            onChange={(e) => setNewProvider(prev => ({ ...prev, modelName: e.target.value }))}
                            placeholder="gpt-4, llama-3.2-3b-instruct, etc."
                            required
                          />
                        </div>
                        <div className={styles.inputGroup}>
                          <label>API Key:</label>
                          <input
                            type="password"
                            value={newProvider.apiKey}
                            onChange={(e) => setNewProvider(prev => ({ ...prev, apiKey: e.target.value }))}
                            placeholder="sk-..."
                            required
                          />
                        </div>
                        <div className={styles.formButtons}>
                          <button type="submit" className={styles.createButton} disabled={llmLoading}>
                            {llmLoading ? 'Erstellen...' : 'Provider erstellen'}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setShowAddProvider(false);
                              setNewProvider({ name: '', apiUrl: '', modelName: '', apiKey: '' });
                            }}
                            className={styles.cancelButton}
                          >
                            Abbrechen
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                )}
              </div>

              {/* AI Prompts Section */}
              <div className={styles.section}>
                <h3>AI Prompts</h3>
                <p className={styles.sectionHint}>
                  Hier können Sie die AI-Prompts anpassen. Der Basis-Prompt ist fest vorgegeben,
                  Ihre Anpassungen werden als zusätzliche Anweisungen hinzugefügt.
                </p>
                <div className={styles.aiPromptButtons}>
                  <button
                    className={styles.aiPromptButton}
                    onClick={() => handleEditPrompt('ticket')}
                  >
                    <i className="bi bi-ticket-detailed"></i>
                    AI Ticket Prompt
                    {aiPrompts.ticket?.custom && <span className={styles.customBadge}>Angepasst</span>}
                  </button>
                  <button
                    className={styles.aiPromptButton}
                    onClick={() => handleEditPrompt('rapport')}
                  >
                    <i className="bi bi-file-earmark-text"></i>
                    AI Rapport Prompt
                    {aiPrompts.rapport?.custom && <span className={styles.customBadge}>Angepasst</span>}
                  </button>
                </div>
              </div>

              {/* External API Key Section (for mail2ticket) */}
              <div className={styles.section}>
                <h3>External API (mail2ticket)</h3>
                <p className={styles.sectionHint}>
                  API Key für externe Dienste wie mail2ticket zum Erstellen von Zoho Desk Tickets.
                </p>
                <div className={styles.apiKeyContainer}>
                  <div className={styles.apiKeyStatus}>
                    <span className={styles.apiKeyLabel}>API Key Status:</span>
                    {externalApiKey.hasKey ? (
                      <span className={styles.apiKeyActive}>
                        Aktiv ({externalApiKey.preview})
                      </span>
                    ) : (
                      <span className={styles.apiKeyInactive}>Nicht generiert</span>
                    )}
                  </div>

                  {generatedApiKey && (
                    <div className={styles.generatedKeyBox}>
                      <p className={styles.generatedKeyLabel}>Neuer API Key (nur einmalig sichtbar!):</p>
                      <div className={styles.generatedKeyValue}>
                        <code>{generatedApiKey}</code>
                        <button
                          onClick={handleCopyApiKey}
                          className={styles.copyButton}
                          title="Kopieren"
                        >
                          <i className="bi bi-clipboard"></i>
                        </button>
                      </div>
                    </div>
                  )}

                  <button
                    onClick={handleGenerateApiKey}
                    className={styles.generateKeyButton}
                    disabled={apiKeyLoading}
                  >
                    {apiKeyLoading ? 'Generiere...' : externalApiKey.hasKey ? 'Neuen Key generieren' : 'API Key generieren'}
                  </button>
                </div>
              </div>
            </>
          )}

          {error && <div className={styles.error}>{error}</div>}
          {success && <div className={styles.success}>{success}</div>}
        </div>

        <div className={styles.footer}>
          <button
            className={styles.closeFooterButton}
            onClick={handleClose}
          >
            Schließen
          </button>
        </div>
      </div>

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={isChangePasswordOpen}
        onClose={() => setIsChangePasswordOpen(false)}
      />

      {/* AI Prompt Edit Modal */}
      {editingPromptType && (
        <div className={styles.promptModalOverlay}>
          <div className={styles.promptModal}>
            <div className={styles.promptModalHeader}>
              <h3>
                {editingPromptType === 'ticket' ? 'AI Ticket Prompt' : 'AI Rapport Prompt'}
              </h3>
              <button className={styles.closeButton} onClick={handleCancelEditPrompt}>×</button>
            </div>

            <div className={styles.promptModalContent}>
              <div className={styles.defaultPromptSection}>
                <label>Basis-Prompt (nicht änderbar):</label>
                <pre className={styles.defaultPromptText}>
                  {aiPrompts[editingPromptType]?.default || 'Lade...'}
                </pre>
              </div>

              <div className={styles.customPromptSection}>
                <label>Zusätzliche Anweisungen (optional):</label>
                <textarea
                  value={editingPromptValue}
                  onChange={(e) => setEditingPromptValue(e.target.value)}
                  placeholder="Fügen Sie hier zusätzliche Anweisungen hinzu, um das AI-Verhalten anzupassen..."
                  className={styles.customPromptTextarea}
                  rows={6}
                />
                <p className={styles.promptHint}>
                  Diese Anweisungen werden dem Basis-Prompt hinzugefügt. Lassen Sie das Feld leer, um nur den Basis-Prompt zu verwenden.
                </p>
              </div>
            </div>

            <div className={styles.promptModalFooter}>
              <button
                className={styles.cancelButton}
                onClick={handleCancelEditPrompt}
                disabled={savingPrompt}
              >
                Abbrechen
              </button>
              <button
                className={styles.savePromptButton}
                onClick={handleSavePrompt}
                disabled={savingPrompt}
              >
                {savingPrompt ? 'Speichern...' : 'Speichern'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}

export default UserSettingsModal;
